import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  type LayoutChangeEvent,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useHomeScreen } from '@/hooks/residents/useHomeScreen';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { formatRelativeDate } from '@/utils/common/formatting';
import { getBalanceStatus } from '@/features/payments/common/helpers';

import {
  GreetingSection,
  QuickActionsSection,
  ComingUpSection,
  CommunityResourcesSection,
  TodoCalendarSection,
  type ComingUpNextEvent,
  type ComingUpRentDue,
  type SqueezeCapacity,
} from '@/components/residents/redesign';

import { DesktopHomeView } from './desktop';

const HP = REDESIGN_SPACING.horizontalPadding;
/** Breathing room under the last row, on top of the device's safe-area inset. */
const BOTTOM_GAP = 16;
/**
 * The MOST todo rows the planner shows before its list starts scrolling. Three
 * is what the mock draws, and what the sections below it leave room for on a
 * roomy phone. The section MEASURES a row to turn this into a height, so the
 * cap holds at any OS font scale — and `squeezeBy` (below) takes height back
 * off it when the page is shorter than that.
 */
const PLANNER_VISIBLE_TODOS = 3;
/**
 * Dead zone for the squeeze loop, in points. Sub-pixel layout rounding (and a
 * mid-animation frame) must not be mistaken for a real overflow, and a
 * correction this small would not move a row anyway.
 */
const SQUEEZE_EPSILON = 2;

export const HomeScreen: React.FC = () => {
  const { layoutMode } = useResponsive();
  if (layoutMode === 'desktop') return <DesktopHomeView />;
  return <MobileHomeView />;
};

/**
 * How the page has decided to fit itself: what it asks of the planner, and
 * whether My Community is on it at all.
 */
export interface PageFit {
  /** Points the planner's list gives back, never past its capacity.toOneRow. */
  squeeze: number;
  /** My Community unmounted; its block comes back once it fits again. */
  hideCommunity: boolean;
  /** The planner's list body dropped outright — the last rung. */
  collapseList: boolean;
}
const NO_FIT: PageFit = { squeeze: 0, hideCommunity: false, collapseList: false };

/**
 * THE FIT LADDER. The page must not scroll, so when it is shorter than its
 * sections something yields — in this order, each rung only once the one
 * before it is spent:
 *   1. the planner's list, from three rows down to one, point by point;
 *   2. My Community, unmounted whole;
 *   3. the planner's list body, dropped outright (the band keeps the count).
 * Only a screen shorter than all three still scrolls. Slack comes back in the
 * reverse order — rows before tiles — and My Community returns only once it
 * fits with a row showing.
 *
 * Decided in ONE pass, not iterated. From a measurement the page recovers its
 * NATURAL overflow — what it would be over by with nothing yielding — by
 * adding back what it has already taken: the planner's shed and the hidden
 * block. Every rung's cost is a known number, so the state read off that is a
 * pure function of the natural overflow, and re-measuring the result
 * reproduces the same decision: there is nothing to oscillate. (The
 * incremental loop this replaced asked for "the overflow" without knowing the
 * planner's floor, so it could neither tell when that was spent nor take a
 * second rung.)
 *
 * The planner's shed is what the page ASKED for, not a guess: a granular
 * capacity is honored exactly up to toOneRow; an all-at-once one (the empty
 * state's compact line) sheds its whole toOneRow on any request, so the page
 * requests exactly that much and its bookkeeping matches. A collapsed list
 * sheds toZero whatever the squeeze says.
 */
export function fitPage({
  overflow,
  prev,
  capacity,
  communityBlock,
}: {
  /** contentHeight − viewportHeight, as measured with `prev` applied. */
  overflow: number;
  prev: PageFit;
  capacity: SqueezeCapacity;
  /** My Community's measured block, the gap above it included. */
  communityBlock: number;
}): PageFit {
  const shed = prev.collapseList
    ? capacity.toZero
    : capacity.granular
      ? Math.min(prev.squeeze, capacity.toOneRow)
      : prev.squeeze > 0
        ? capacity.toOneRow
        : 0;
  const natural = overflow + shed + (prev.hideCommunity ? communityBlock : 0);
  const fits = (short: number) => short <= SQUEEZE_EPSILON;
  const rungOne = (short: number) =>
    capacity.granular ? Math.min(short, capacity.toOneRow) : capacity.toOneRow;

  let next: PageFit;
  if (fits(natural)) {
    next = NO_FIT;
  } else if (fits(natural - capacity.toOneRow)) {
    next = { squeeze: rungOne(natural), hideCommunity: false, collapseList: false };
  } else {
    const rest = natural - communityBlock;
    if (fits(rest)) {
      next = { squeeze: 0, hideCommunity: true, collapseList: false };
    } else if (fits(rest - capacity.toOneRow)) {
      next = { squeeze: rungOne(rest), hideCommunity: true, collapseList: false };
    } else {
      next = { squeeze: capacity.toOneRow, hideCommunity: true, collapseList: true };
    }
  }
  // Dead zone: the same rungs and a squeeze within ε is sub-pixel layout
  // noise (or a mid-animation frame), not a correction — it would not move a
  // row. Returning `prev` keeps the state, and so the render, unchanged.
  const same =
    next.hideCommunity === prev.hideCommunity &&
    next.collapseList === prev.collapseList &&
    Math.abs(next.squeeze - prev.squeeze) <= SQUEEZE_EPSILON;
  return same ? prev : next;
}

/**
 * Runs the ladder off the page's measurements. Four inputs: the viewport and
 * content heights (layout events, on the page), the planner's capacity (its
 * report) and My Community's block (a layout event, on its wrapper).
 *
 * WHEN TO DECIDE is the subtle part. A page measurement is decided on the
 * spot: the planner reports from a layout effect, so by the time the layout
 * events of a commit arrive its report for that commit is already in. A
 * report or a block measurement, on the other hand, may land BEFORE the page
 * measurement of the same commit (a day change re-reports and re-measures at
 * once), so deciding on it right away would pair fresh inputs with a stale
 * overflow. Those wait two animation frames — long enough for the commit's
 * layout events to arrive, and nothing at all when there are none (a report
 * whose commit did not change the page's height, which is exactly the case
 * the deferred decision exists for).
 */
function useFitToPage() {
  const [viewportHeight, setViewportHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [fit, setFit] = useState<PageFit>(NO_FIT);
  // Mirrors, so a deferred decision reads the latest of everything without
  // re-subscribing to it.
  const viewportRef = useRef(0);
  const contentRef = useRef(0);
  const capacityRef = useRef<SqueezeCapacity | null>(null);
  const communityRef = useRef<number | null>(null);
  const frameRef = useRef<number | null>(null);

  const decide = useCallback(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    const capacity = capacityRef.current;
    const communityBlock = communityRef.current;
    // Half a picture decides nothing: unmeasured (first frame), or the planner
    // has not reported yet (loading), or the block is not measured yet.
    if (viewport <= 0 || content <= 0 || !capacity || communityBlock === null) return;
    setFit(prev => fitPage({ overflow: content - viewport, prev, capacity, communityBlock }));
  }, []);

  const scheduleDecide = useCallback(() => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        decide();
      });
    });
  }, [decide]);
  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  // No-op on an unchanged height, so a repeated layout pass cannot re-run
  // the decision below.
  const handleViewportLayout = useCallback((e: LayoutChangeEvent) => {
    const { height } = e.nativeEvent.layout;
    viewportRef.current = height;
    setViewportHeight(prev => (prev === height ? prev : height));
  }, []);
  const handleContentLayout = useCallback((e: LayoutChangeEvent) => {
    const { height } = e.nativeEvent.layout;
    contentRef.current = height;
    setContentHeight(prev => (prev === height ? prev : height));
  }, []);
  // A page measurement: decided on the spot (see above).
  useEffect(() => {
    decide();
  }, [viewportHeight, contentHeight, decide]);

  const handleCapacity = useCallback(
    (capacity: SqueezeCapacity) => {
      capacityRef.current = capacity;
      scheduleDecide();
    },
    [scheduleDecide],
  );
  const handleCommunityLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const { height } = e.nativeEvent.layout;
      if (height <= 0) return;
      communityRef.current = height;
      scheduleDecide();
    },
    [scheduleDecide],
  );

  return { fit, handleViewportLayout, handleContentLayout, handleCapacity, handleCommunityLayout };
}

/**
 * Mobile home. The page is sized to fit, and every section on it is
 * intrinsically sized: the planner is capped at PLANNER_VISIBLE_TODOS rows and
 * scrolls its own list past them instead of stretching into whatever height is
 * left. The rows therefore top-align, and any leftover height falls to the
 * bottom of the page rather than being absorbed by a card.
 *
 * On a page too short for all of that, things yield in a fixed order — the
 * fit ladder in `useFitToPage`: the planner's list first, from three rows
 * down to one (its own scroller and bottom fade take over); then My
 * Community, which unmounts whole; then the planner's list body outright, the
 * band keeping the count. Only a screen shorter than all three still scrolls.
 *
 * Layout shape, which the measurement depends on: the ScrollView is the
 * VIEWPORT and holds no padding of its own; a single inner column View wraps
 * every section and carries all of it, so that column's measured height IS the
 * true content height, insets included. (The page container is still a
 * ScrollView purely as a last-resort overflow fallback: with `flexGrow: 1`
 * content and no bounce it does not scroll at all while everything fits, but
 * when it genuinely cannot — landscape, split screen, accessibility type
 * scaling, small phones; `layoutMode` only says 'desktop' on web ≥1024, so
 * tablets land here too — the rows below the planner stay reachable instead of
 * rendering past the screen edge. Same reasoning as the desktop view's
 * right-hand column.)
 */
const MobileHomeView: React.FC = () => {
  const insets = useSafeAreaInsets();
  const { fit, handleViewportLayout, handleContentLayout, handleCapacity, handleCommunityLayout } =
    useFitToPage();
  const {
    greeting,
    firstName,
    unitName,
    propertyImageUrl,
    newsfeedNewCount,
    unreadMessagesCount,
    surveysToAnswerCount,
    nextUpcomingEvent,
    balanceDue,
    openTickets,
    shouldShowPayments,
    handleBulletinPress,
    handleEventPress,
    handleEventsListPress,
    handleSurveysPress,
    handlePlannerPress,
    handleWiFiPress,
    handleLinkedAccountsPress,
    handleMarketplacePress,
    handlePaymentsPress,
    handleMaintenancePress,
    handleMessagesPress,
    handleResourcesPress,
  } = useHomeScreen();

  // No due date is reachable from the home screen today: the lease balance
  // endpoint returns charge POSTING dates, not a rent due date, so the row
  // ships with the amount only (ComingUpSection renders it without a date).
  // A negative balance is a credit: it still gets a row, which the section
  // titles and inks for it. The status is decided here (it reads the day of
  // the month) so the section stays a pure presenter.
  const rentDue = useMemo<ComingUpRentDue | null>(
    () =>
      shouldShowPayments && Number.isFinite(balanceDue) && balanceDue !== 0
        ? { amount: balanceDue, status: getBalanceStatus(balanceDue) }
        : null,
    [shouldShowPayments, balanceDue],
  );

  const nextEvent = useMemo<ComingUpNextEvent | null>(() => {
    if (!nextUpcomingEvent) return null;
    return {
      title: nextUpcomingEvent.name || 'Community event',
      dateLabel: formatRelativeDate(
        nextUpcomingEvent.startTimeLocal || nextUpcomingEvent.startTime,
      ),
    };
  }, [nextUpcomingEvent]);

  const handleNextEventPress = useCallback(() => {
    if (nextUpcomingEvent) handleEventPress(nextUpcomingEvent);
  }, [nextUpcomingEvent, handleEventPress]);

  return (
    <ScrollView
      style={styles.page}
      contentContainerStyle={styles.pageContent}
      // No bounce: while the content fits, this must feel like the fixed page
      // it is, not a scroller.
      alwaysBounceVertical={false}
      showsVerticalScrollIndicator={false}
      // The viewport half of the squeeze measurement — the ScrollView's own
      // frame, which is what the content has to fit inside.
      onLayout={handleViewportLayout}
    >
      {/* The content half. Every section lives in here and every point of page
          padding is on this one box, so its measured height is exactly what the
          viewport has to hold — no insets left outside the number. Intrinsic on
          purpose: a flex:1 here would report the (flexGrow'd) container height
          instead of the content's, and measure nothing at all. */}
      <View
        style={[styles.column, { paddingBottom: insets.bottom + BOTTOM_GAP }]}
        onLayout={handleContentLayout}
      >
        <GreetingSection
          greeting={greeting}
          firstName={firstName}
          unitName={unitName}
          hasImage={!!propertyImageUrl}
          // heading2, not heading1: on the no-scroll page a 28pt greeting is
          // height the planner does not get.
          dense
          style={styles.greeting}
        />

        {/* As tall as its chrome plus up to three todo rows — fewer when the
            page is short and the ladder claims some of that height back, none
            at its last rung; a row past the bound scrolls inside the card
            rather than pushing the sections below off the page. */}
        <View>
          <TodoCalendarSection
            maxVisibleTodos={PLANNER_VISIBLE_TODOS}
            squeezeBy={fit.squeeze}
            collapseList={fit.collapseList}
            onSqueezeCapacity={handleCapacity}
            denseHeader
            onViewAll={handlePlannerPress}
          />
        </View>

        <QuickActionsSection
          openTicketsCount={openTickets?.length || 0}
          newsfeedNewCount={newsfeedNewCount}
          unreadMessagesCount={unreadMessagesCount}
          surveysCount={surveysToAnswerCount}
          onMaintenance={handleMaintenancePress}
          onNewsfeed={handleBulletinPress}
          onMessages={handleMessagesPress}
          onSurveys={handleSurveysPress}
          denseHeader
          style={styles.section}
        />

        <ComingUpSection
          rentDue={rentDue}
          onRentDuePress={handlePaymentsPress}
          nextEvent={nextEvent}
          onNextEventPress={handleNextEventPress}
          denseHeader
          style={styles.section}
        />

        {/* The ladder's second rung: gone entirely on a page too short for
            it even with the planner at one row, back as soon as it fits again.
            Its destinations stay a menu tap away either way. The block's own
            paddingTop carries the gap above it, so the height measured here
            is exactly what hiding it frees (a marginTop would be left out). */}
        {fit.hideCommunity ? null : (
          <View
            style={styles.communityBlock}
            onLayout={handleCommunityLayout}
            testID="community-block"
          >
            <CommunityResourcesSection
              onMarketplace={handleMarketplacePress}
              onResources={handleResourcesPress}
              // Always the list now — the Coming Up row owns the deep link into
              // a single event, so this tile no longer has to guess.
              onEvents={handleEventsListPress}
              onWifi={handleWiFiPress}
              onLinkedAccountsPress={handleLinkedAccountsPress}
              denseHeader
            />
          </View>
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
  },
  pageContent: {
    // At least one screen tall while everything fits, so nothing scrolls and
    // any leftover height sits below the last section; taller — and scrollable
    // — only when the sections genuinely do not fit. No padding here: it all
    // belongs to the measured column below.
    flexGrow: 1,
  },
  column: {
    paddingHorizontal: HP,
  },
  greeting: {
    // Tighter than the section's own 24/16 default: on a no-scroll page every
    // point above the planner comes out of the todo list.
    paddingTop: 8,
    marginBottom: 8,
  },
  section: {
    marginTop: REDESIGN_SPACING.contentGap,
  },
  communityBlock: {
    paddingTop: REDESIGN_SPACING.contentGap,
  },
});

export { HomeScreen as ResidentHomeScreen };
export default HomeScreen;
